import {
	DOCUMENT_LIBRARY_ELEMENT_ID,
	makeComplexDataTestId,
} from '@dealstack/shared/tests/testElementIds';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput';
import { MultiSelect, Select, TextInput } from 'react-hook-form-mantine';
import { FormField } from './types';
import { Text } from '@mantine/core';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch';
import { SearchSelectWithNestedCollapsableChildren } from '@dealstack/frontend/shared/components/forms/SearchSelectWithNestedCollapsableChildren/SearchSelectWithNestedCollapsableChildren';
import {
	hasOptions,
	mapFieldOptions,
} from '@dealstack/frontend/shared/components/Documents/FormFields/utils';

export const EditField = <T extends FieldValues>({
	field,
	control,
}: {
	field: FormField;
	control: Control<T>;
}) => {
	switch (field.type) {
		case 'text':
			return (
				<Controller
					control={control}
					name={field.fieldKey as Path<T>}
					rules={{ required: field.required }}
					render={({ field: { ref: _, ...controllerField } }) => (
						<TextInput
							data-testid={makeComplexDataTestId(
								DOCUMENT_LIBRARY_ELEMENT_ID.FIELD_INPUT,
								field.fieldKey as string
							)}
							label={field.label}
							{...controllerField}
							required={field.required}
							readOnly={field.readOnly}
						/>
					)}
				/>
			);

		case 'select':
			return (
				<Controller
					control={control}
					name={field.fieldKey as Path<T>}
					render={({ field: controllerField }) => (
						<Select
							{...controllerField}
							{...field}
							data={hasOptions(field) ? field.options : []}
						/>
					)}
				/>
			);

		case 'multiSelect':
			return (
				<Controller
					control={control}
					name={field.fieldKey as Path<T>}
					render={({ field: controllerField }) => (
						<MultiSelect
							// Mantine's own data-testid lands on a 1px search input that cannot
							// receive clicks; the wrapper is the element users actually hit.
							wrapperProps={{
								'data-testid': makeComplexDataTestId(
									DOCUMENT_LIBRARY_ELEMENT_ID.FIELD_INPUT,
									field.fieldKey as string
								),
							}}
							{...controllerField}
							{...field}
							data={hasOptions(field) ? field.options : []}
							size="xs"
							styles={{
								input: {
									paddingTop: '0.4rem',
									paddingBottom: '0.4rem',
									display: 'flex',
									alignItems: 'center',
									minHeight: '34px',
								},
							}}
						/>
					)}
				/>
			);

		case 'multiSelectWithNestedCollapsableChildren':
			return (
				<Controller
					control={control}
					name={field.fieldKey as Path<T>}
					render={({ field: controllerField }) => {
						return (
							<SearchSelectWithNestedCollapsableChildren
								{...controllerField}
								inputTitle={'inputTitle' in field ? String(field.inputTitle) : ''}
								placeHolderText={field.placeholder}
								searchPlaceHolderText={
									'searchPlaceHolderText' in field
										? String(field.searchPlaceHolderText)
										: ''
								}
								options={mapFieldOptions(field)}
								allowMultiOptionSelect={false}
							/>
						);
					}}
				/>
			);

		case 'date':
			return (
				<Controller
					control={control}
					name={field.fieldKey as Path<T>}
					rules={{ required: field.required }}
					render={({ field: controllerField }) => (
						<MantineDateInput
							label={field.label}
							{...controllerField}
							required={field.required}
							type="date"
						/>
					)}
				/>
			);
		case 'boolean':
			return (
				<Controller
					control={control}
					name={field.fieldKey as Path<T>}
					rules={{ required: field.required }}
					render={({ field: { value, onChange } }) => (
						<>
							<Text c="dark.0" pb="0.5rem">
								{field.label}
							</Text>
							<AdvancedSwitch
								selectedValue={Boolean(value)}
								onChange={(next) => onChange(next)}
								disabled={field.disabled}
							/>
						</>
					)}
				/>
			);

		default:
			return null;
	}
};
